
import Vue from 'vue';
import { VueGoodTable } from 'vue-good-table';
import 'vue-good-table/dist/vue-good-table.css';
import { Component, Prop } from 'vue-property-decorator';
import Toasted from 'vue-toasted';
import { handleError, initClient } from '../../../api';
import { formatDateTime, WindowType } from '../../../util';
import { SurveyJsUiClient, SurveyResultView, SurveyView } from '../../backend/backendapi';
import { paginationOptions, searchOptions } from '../../../util/vue-good-table';
import SignatureModal from './SignatureModal';
import { getSignatureImage } from './util';
import AnswersModal from './AnswersModal';
import Visible from '../../../directives/visible';
import { Results, VgtParameters, VgtRemoteManger } from './VgtRemoteManger';
import { Like } from '../../../util/Like';
import Spinner from '../../util/Spinner';
import { getLocalizedSurveyNameFromSurvey } from '../utils';

declare var window: WindowType;

Vue.use(Toasted, { duration: 2000 });

@Component({
  name: 'SurveyResultsComponent',
  methods: { getLocalizedSurveyNameFromSurvey },
  components: {
    Spinner,
    AnswersModal,
    SignatureModal,
    VueGoodTable,
  },
  directives: {
    Visible,
  },
})
export default class ResultsTable extends Vue {
  @Prop({ required: true })
  loadingFunction!: (params: VgtParameters) => Promise<Results<SurveyResultView>>;
  @Prop({ default: false })
  showName!: boolean;

  surveys: SurveyView[] = [];
  vgtManager = new VgtRemoteManger<SurveyResultView>(this.loadResults.bind(this));
  settingEndDateIds = new Set();

  private readonly api = initClient(SurveyJsUiClient);

  get canSetEndDate(): boolean {
    return window.application.permissions.survey.setResultEndDate;
  }

  get columnsConfig(): any[] {
    return [
      {
        label: this.$t('firstname').toString(),
        field: 'participantFirstname',
      },
      {
        label: this.$t('lastname').toString(),
        field: 'participantLastname',
      },
      {
        label: this.$t('email').toString(),
        field: 'participantEmail',
      },
      {
        label: this.$t('company').toString(),
        field: 'participantCompany',
      },
      this.showName
          ? { // Only includes this object if showName is true
            label: this.$t('surveySurveys').toString(),
            field: 'name',
          }
          : null,
      {
        label: this.$t('surveyStartDate').toString(),
        field: 'startDate',
        type: 'date',
        formatFn: formatDateTime,
      },
      {
        label: this.$t('surveyDoneDate').toString(),
        field: 'doneDate',
        type: 'date',
        formatFn: formatDateTime,
        doneDateColumn: true,
      },
      {
        label: this.$t('survey.result.validTil').toString(),
        // Logic: Only calculate date if passed AND validTilSec is a positive number
        field: (e: SurveyResultView) => {
          if (e.isPassed && e.validTilSec && e.validTilSec > 0) {
            return new Date(e.doneDate.getTime() + e.validTilSec * 1000);
          }
          return null; // Return null for invalid/zero durations
        },
        type: 'date',
        formatFn: (value: Date | null) => value ? formatDateTime(value) : '-',
        doneDateColumn: true,
      },
      {
        label: this.$t('surveyPassed').toString(),
        field: 'isPassed',
        formatFn: (value: boolean) => value ? this.$t('yes') : this.$t('no'),
        passedColumn: true,
      },
    ].filter(Boolean);
  }

  get paginationOptions(): any {
    return paginationOptions(this.$i18n);
  }

  get searchOptions(): any {
    return searchOptions(this.$i18n);
  }

  get sortOptions(): any {
    return {
      enabled: true,
      initialSortBy: { field: 'startDate', type: 'desc' },
    };
  }

  get surveysById(): Map<string, SurveyView> {
    return new Map(this.surveys.map(e => [e.id, e]));
  }

  private async reloadSurveys(results: SurveyResultView[]): Promise<void> {
    const surveyIds = [...new Set(results.map(result => result.surveyId))];
    const surveys = await Promise.allSettled(surveyIds.map(id => this.api.getSurvey(id)));
    this.surveys.splice(0);
    this.surveys.push(...surveys.filter(e => e.status == 'fulfilled').map(e => (e as PromiseFulfilledResult<SurveyView>).value));
  }

  private async loadResults(params: VgtParameters): Promise<Results<SurveyResultView>> {
    const data = await this.loadingFunction(params);
    await this.reloadSurveys(data.data);
    return data as Results<SurveyResultView>;
  }

  hasAssemblyPoint(result: SurveyResultView): boolean {
    const extra = JSON.parse(result.extraPropertiesJson);
    return (typeof extra.assemblyPoint === 'boolean' && extra.assemblyPoint !== false)
        || (typeof extra.assemblyPoint === 'number' && extra.assemblyPoint !== 0)
        || (typeof extra.assemblyPoint === 'string' && extra.assemblyPoint.length > 0)
        || (typeof extra.assemblyPoint === 'object' && extra.assemblyPoint && Object.keys(extra.assemblyPoint).length > 0)
        || (Array.isArray(extra.assemblyPoint) && extra.assemblyPoint.length > 0);
  }

  hasSignature(result: SurveyResultView): boolean {
    const survey = this.surveysById.get(result.surveyId);
    return !!survey && getSignatureImage(survey, result).length > 0;
  }

  async setEndDate(result: Like<SurveyResultView>): Promise<void> {
    this.settingEndDateIds.add(result.id);
    try {
      await this.api.setEndDate(result.surveyId, result.id);
      this.settingEndDateIds.delete(result.id); // needs to be removed before the reload happens, otherwise the button will continue to show the spinner
      await this.vgtManager.reload();
    } catch (error) {
      handleError(this.$toasted, this.$i18n, error);
    } finally {
      this.settingEndDateIds.delete(result.id);
    }
  }

  showAnswers(result: SurveyResultView): void {
    const survey = this.surveysById.get(result.surveyId);
    if (!survey) {
      return;
    }
    (this.$refs.answerModal as AnswersModal).open(survey.id, result.id);
  }

  showSignature(result: SurveyResultView): void {
    const survey = this.surveysById.get(result.surveyId);
    if (!survey) {
      return;
    }
    (this.$refs.signatureModal as SignatureModal).open(survey, result);
  }

  get visibleSurveys(): SurveyView[] {
    return this.surveys ? this.surveys.slice(0, 2) : [];
  }

  get extraSurveysCount(): number {
    return this.surveys && this.surveys.length > 2 ? this.surveys.length - 2 : 0;
  }
}
